/* eslint-disable react/no-unused-state */
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import omit from 'lodash/omit';

import { setBackgroundImageUrl } from 'ggApp/utils/styling';
import withIntersectionObserver from 'ggApp/modules/tracking/hocs/withIntersectionObserverV2';
import { ImageContainer, Root, Shimmer } from './styles';

const DELAY_VALUES = [0, 0.2, 0.4, 0.6, 0.8, 1, 1.2, 1.4, 1.6];

const loadImage = (uri) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.src = uri;
        if (img.decode !== undefined) {
            img.decode()
                .then(() => {
                    resolve(img.src);
                })
                .catch(() => {
                    reject(new Error('An Error occurred while trying to decode an image'));
                });
        } else {
            img.onload = () => {
                resolve(img.src);
            };
        }
        img.onerror = () => {
            reject(new Error('An Error occurred while trying to download an image'));
        };
    });

/**
 * ShimmerEffect wrapper
 * @param ImageComponent: ProductResolutionImage | ContentfulResolutionImage | undefined
 * function returns RC with Shimmer effect when the image is loading based on given component:
 * if the ResolutionImage type based component is given,
 *   then imgUrls are in order, will be used there
 * if undefined is given or imgUrls is empty,
 *   then Shimmer effect will handle image render by itself as a div's background-image
 * @returns ShimmerEffect RC
 */
const ShimmerEffect = (ImageComponent) => {
    const ImageWithObserver = withIntersectionObserver(ImageComponent);
    class ShimmerEffectImage extends PureComponent {
        // eslint-disable-next-line react/static-property-placement
        static propTypes = {
            className: PropTypes.string,
            accentColor: PropTypes.string,
            src: PropTypes.string,
            imgUrls: PropTypes.oneOfType([
                PropTypes.shape({
                    mini: PropTypes.string,
                    small: PropTypes.string,
                    dashboard: PropTypes.string,
                    medium: PropTypes.string,
                    large: PropTypes.string,
                    x_large: PropTypes.string,
                    x2_large: PropTypes.string,
                }),
                PropTypes.string,
            ]).isRequired,
            sizes: PropTypes.shape({
                mobile: PropTypes.string,
                tablet: PropTypes.string,
                desktop: PropTypes.string,
                largeDesktop: PropTypes.string,
                default: PropTypes.string,
            }),
            alt: PropTypes.string,
            lazy: PropTypes.bool,
        };

        // eslint-disable-next-line react/static-property-placement
        static defaultProps = {
            className: '',
            sizes: undefined,
            accentColor: undefined,
            src: null,
            lazy: true,
            alt: '',
        };

        constructor(props) {
            super(props);
            if (!this.animationDelay) {
                this.animationDelay = DELAY_VALUES[Math.floor(Math.random() * DELAY_VALUES.length)];
            }
            this.state = {
                loading: true,
            };
        }

        componentDidMount() {
            const { src, imgUrls } = this.props;
            if (!imgUrls && src) {
                loadImage(src)
                    .then((uri) => {
                        this.setState({ src: uri }, () => this.setState({ loading: false }));
                    })
                    .catch((error) => {
                        this.setState({ error, loading: false });
                    });
            }
            if (!imgUrls && !src) {
                this.setState({ error: 'empty sources', loading: false });
            }
        }

        finishLoading = () => {
            this.setState({ loading: false });
        };

        onLoadhandler = (e) => {
            const { onLoad } = this.props;
            this.finishLoading();
            if (typeof onLoad === 'function') {
                onLoad(e);
            }
        };

        onEnterCallback = () => {
            const { entered } = this.state;
            if (entered) {
                return;
            }
            this.setState({ entered: true });
        };

        render() {
            const {
                imgUrls,
                className,
                sizes,
                accentColor,
                src: propsSrc,
                imgClassName,
                lazy,
                alt,
            } = this.props;
            const { loading, src, entered } = this.state;
            const rootStyle = !imgUrls
                ? {
                      backgroundImage: setBackgroundImageUrl(src),
                  }
                : {};
            const imgProps = {
                ...omit(this.props, [
                    'onLoad',
                    'src',
                    'imgUrls',
                    'sizes',
                    'className',
                    'imgClassName',
                    'accentColor',
                ]),
            };
            return (
                <Root className={className} style={rootStyle}>
                    <ImageContainer className={imgClassName}>
                        {imgUrls && ImageComponent && (
                            <ImageWithObserver
                                {...imgProps}
                                src={propsSrc}
                                imgUrls={imgUrls}
                                sizes={sizes}
                                onLoad={this.onLoadhandler}
                                lazy={lazy}
                                alt={alt}
                                onEnterCallback={this.onEnterCallback}
                                threshold={0.01}
                            />
                        )}
                    </ImageContainer>
                    <Shimmer
                        animationDelay={this.animationDelay}
                        accentColor={accentColor}
                        loading={loading}
                        entered={entered}
                    />
                </Root>
            );
        }
    }
    return ShimmerEffectImage;
};

export default ShimmerEffect;
